import { Geodesic } from "geographiclib-geodesic"

export type Coordinate = [number, number]
const earth = Geodesic.WGS84

export function destinationPoint(
  lat: number, lon: number, bearing: number, meters: number
): Coordinate {
  const point = earth.Direct(lat, lon, bearing, meters)
  // Keep the same world copy when the track crosses the antimeridian.
  const longitude = point.lon2! + 360 * Math.round((lon - point.lon2!) / 360)
  return [point.lat2!, longitude]
}

export function computeBearing(
  fromLat: number, fromLon: number, toLat: number, toLon: number
): number {
  const longitudeDelta = (toLon - fromLon) * Math.PI / 180
  const dx = Math.atan2(Math.sin(longitudeDelta), Math.cos(longitudeDelta))
  const dy = mercatorY(toLat) - mercatorY(fromLat)
  return (Math.atan2(dx, dy) * 180 / Math.PI + 360) % 360
}

export function buildRangeRing(lat: number, lon: number, radius: number): Coordinate[] {
  return Array.from({ length: 121 }, (_, i) => destinationPoint(lat, lon, i * 3, radius))
}

const mercatorY = (lat: number) => Math.log(Math.tan(Math.PI / 4 + lat * Math.PI / 360))

// Parameterize straight Leaflet track lines in Web Mercator, retaining world copy.
function mapPoint(origin: Coordinate, bearing: number, distance: number): Coordinate {
  const angle = bearing * Math.PI / 180
  return [
    (2 * Math.atan(Math.exp(mercatorY(origin[0]) + Math.cos(angle) * distance)) - Math.PI / 2) * 180 / Math.PI,
    origin[1] + Math.sin(angle) * distance * 180 / Math.PI,
  ]
}

function rangeIntersection(
  center: Coordinate, origin: Coordinate, bearing: number, radius: number
): Coordinate {
  let low = 0
  let high = 4 * radius / (6378137 * Math.cos(center[0] * Math.PI / 180))
  for (let i = 0; i < 32; i++) {
    const distance = (low + high) / 2
    const point = mapPoint(origin, bearing, distance)
    if (earth.Inverse(...center, ...point).s12! <= radius) low = distance
    else high = distance
  }
  return mapPoint(origin, bearing, (low + high) / 2)
}

export function buildConePolygon(
  lat: number, lon: number, heading: number, radius: number,
  halfAngle: number, sampleCount = 60
): Coordinate[] {
  const center: Coordinate = [lat, lon]
  const apex = rangeIntersection(center, center, heading + 180, radius)
  const arc = Array.from({ length: sampleCount + 1 }, (_, i) =>
    rangeIntersection(center, apex, heading - halfAngle + 2 * halfAngle * i / sampleCount, radius))
  return [apex, ...arc]
}
